import { lazy, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { NetworkStatusProvider } from "@/contexts/NetworkStatusContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequireRole } from "@/components/RequireRole";
import { RequireSafeguardingAccess } from "@/components/RequireSafeguardingAccess";
import { Layout } from "@/components/Layout";
import { PlanGate } from "@/components/PlanGate";
import { PageLoadingSkeleton } from "@/components/PageLoadingSkeleton";
import { BodyPointerEventsGuard } from "@/components/BodyPointerEventsGuard";

// Lazy-loaded pages
const RecordsDownloadPage = lazy(() => import("./pages/RecordsDownloadPage"));
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"));
const ChildrenList = lazyWithRetry(() => import("@/pages/ChildrenList"));
const ChildProfile = lazyWithRetry(() => import("@/pages/ChildProfile"));
const ZoneDetail = lazyWithRetry(() => import("@/pages/ZoneDetail"));
const TransitionsList = lazyWithRetry(() => import("@/pages/TransitionsList"));
const StaffList = lazyWithRetry(() => import("@/pages/StaffList"));
const StaffProfile = lazyWithRetry(() => import("@/pages/StaffProfile"));
const RotaPage = lazyWithRetry(() => import("@/pages/RotaPage"));
const RegisterPage = lazyWithRetry(() => import("@/pages/RegisterPage"));
const FundingPage = lazyWithRetry(() => import("@/pages/FundingPage"));
const ReportsPage = lazyWithRetry(() => import("@/pages/ReportsPage"));
const AlertsPage = lazyWithRetry(() => import("@/pages/AlertsPage"));
const CompliancePage = lazyWithRetry(() => import("@/pages/CompliancePage"));
const AttendanceStaffingPage = lazyWithRetry(() => import("@/pages/AttendanceStaffingPage"));
const PlannedChangesPage = lazyWithRetry(() => import("@/pages/PlannedChangesPage"));
const AdditionalDaysParentPage = lazyWithRetry(() => import("./pages/AdditionalDaysParentPage"));
const EnrolmentParentPage = lazyWithRetry(() => import("./pages/EnrolmentParentPage"));
const StaffOnboardingPage = lazyWithRetry(() => import("./pages/StaffOnboardingPage"));
const AdditionalDaysPage = lazyWithRetry(() => import("@/pages/AdditionalDaysPage"));
const SafeguardingLogPage = lazyWithRetry(() => import("@/pages/SafeguardingLogPage"));
const ComplaintsLogPage = lazyWithRetry(() => import("@/pages/ComplaintsLogPage"));
const ConfidentialSafeguardingPage = lazyWithRetry(() => import("@/pages/ConfidentialSafeguardingPage"));
const SettingsPage = lazyWithRetry(() => import("@/pages/SettingsPage"));
const AccessManagement = lazyWithRetry(() => import("@/pages/AccessManagement"));
const PermissionsGuidePage = lazyWithRetry(() => import("@/pages/PermissionsGuidePage"));
const MyProfilePage = lazyWithRetry(() => import("@/pages/MyProfilePage"));
const SecurityPage = lazyWithRetry(() => import("@/pages/SecurityPage"));
const AuditLogPage = lazyWithRetry(() => import("@/pages/AuditLogPage"));
const OfstedReadinessPage = lazyWithRetry(() => import("@/pages/OfstedReadinessPage"));
const ApprovalsPage = lazyWithRetry(() => import("@/pages/ApprovalsPage"));
const OnboardingPage = lazyWithRetry(() => import("@/pages/OnboardingPage"));
const ExploreChoicePage = lazyWithRetry(() => import("@/pages/ExploreChoicePage"));

const HelpManualPage = lazyWithRetry(() => import("@/pages/HelpManualPage"));
const MyRotaPage = lazyWithRetry(() => import("@/pages/MyRotaPage"));
const RoomRegisterPage = lazyWithRetry(() => import("@/pages/RoomRegisterPage"));
const TodaysPickupsPage = lazyWithRetry(() => import("@/pages/TodaysPickupsPage"));
const MonthlyOverviewPage = lazyWithRetry(() => import("@/pages/MonthlyOverviewPage"));
const SchoolsPage = lazyWithRetry(() => import("@/pages/SchoolsPage"));
const BookingManagementPage = lazyWithRetry(() => import("@/pages/BookingManagementPage"));
const BookingFormPage = lazyWithRetry(() => import("@/pages/BookingFormPage"));
const NurslyAdminMigrationsPage = lazyWithRetry(() => import("@/pages/NurslyAdminMigrationsPage"));
const NurslyAdminErrorLogPage = lazyWithRetry(() => import("@/pages/NurslyAdminErrorLogPage"));
const NurslyAdminFeedbackPage = lazyWithRetry(() => import("@/pages/NurslyAdminFeedbackPage"));
const NurslyAdminDebugEmailsPage = lazyWithRetry(() => import("@/pages/NurslyAdminDebugEmailsPage"));
const UpgradePage = lazyWithRetry(() => import("@/pages/UpgradePage"));
const OutingsPage = lazyWithRetry(() => import("@/pages/OutingsPage"));
const Login = lazyWithRetry(() => import("@/pages/Login"));
const SignupPage = lazyWithRetry(() => import("@/pages/SignupPage"));
const UnsubscribePage = lazyWithRetry(() => import("@/pages/UnsubscribePage"));
const VerifyEmailPage = lazyWithRetry(() => import("@/pages/VerifyEmailPage"));
const VerifyEmailConfirmed = lazy(async () => {
  const mod = await import("@/pages/VerifyEmailPage");
  return { default: mod.VerifyEmailConfirmedPage };
});
const ForgotPasswordPage = lazyWithRetry(() => import("@/pages/ForgotPasswordPage"));
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"));
const AccountSetup = lazyWithRetry(() => import("@/pages/AccountSetup"));
const AcceptInvitePage = lazyWithRetry(() => import("@/pages/AcceptInvitePage"));
const NurslyLandingPage = lazyWithRetry(() => import("@/pages/NurslyLandingPage"));
const TermsPage = lazyWithRetry(() => import("@/pages/TermsPage"));
const PrivacyPage = lazyWithRetry(() => import("@/pages/PrivacyPage"));
const PublicSecurityPage = lazyWithRetry(() => import("@/pages/PublicSecurityPage"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const KioskPage = lazyWithRetry(() => import("@/pages/KioskPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      // B1-03: refetching on every tab refocus caused list pages to re-render
      // and lose the user's scroll position. Data stays fresh via realtime
      // invalidation, route changes and the 30s staleTime; individual queries
      // that genuinely need focus refetching opt in explicitly.
      refetchOnWindowFocus: false,
    },
  },
});

function PageBoundary({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <ErrorBoundary sectionName={name} level="page">
      {children}
    </ErrorBoundary>
  );
}

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BodyPointerEventsGuard />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <NetworkStatusProvider>
          <AuthProvider>
            <Suspense fallback={<PageLoadingSkeleton />}>
              <Routes>
                <Route path="/" element={<NurslyLandingPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/security" element={<PublicSecurityPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-email/confirmed" element={<VerifyEmailConfirmed />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/account-setup" element={<AccountSetup />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/booking/:nurserySlug/after-school-club" element={<BookingFormPage />} />
                <Route path="/booking/after-school-club" element={<BookingFormPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/additional-days/:token" element={<PageBoundary name="Additional Days Request"><AdditionalDaysParentPage /></PageBoundary>} />
                <Route path="/enrolment/:token" element={<PageBoundary name="Enrolment Form"><EnrolmentParentPage /></PageBoundary>} />
                <Route path="/staff-onboarding/:token" element={<PageBoundary name="New Starter Form"><StaffOnboardingPage /></PageBoundary>} />
                <Route path="/kiosk" element={<PageBoundary name="Kiosk"><KioskPage /></PageBoundary>} />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <PageBoundary name="Onboarding">
                      <OnboardingPage />
                    </PageBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/explore-choice" element={
                  <ProtectedRoute>
                    <PageBoundary name="Choose how to start">
                      <ExploreChoicePage />
                    </PageBoundary>
                  </ProtectedRoute>
                } />

                <Route path="/*" element={
                  <ProtectedRoute>
                    <Layout>
                      <Suspense fallback={<PageLoadingSkeleton />}>
                        <Routes>
                          <Route path="/dashboard" element={<PageBoundary name="Dashboard"><Dashboard /></PageBoundary>} />
                          <Route path="/children" element={<PageBoundary name="Children"><ChildrenList /></PageBoundary>} />
                          <Route path="/children/:id" element={<PageBoundary name="Child Profile"><ChildProfile /></PageBoundary>} />
                          <Route path="/zone/:zoneId" element={<PageBoundary name="Zone Detail"><ZoneDetail /></PageBoundary>} />
                          <Route path="/transitions" element={<PageBoundary name="Transitions"><PlanGate feature="Transitions" description="Plan and track children's moves between rooms and zones, with approvals and key person handover, on Professional."><TransitionsList /></PlanGate></PageBoundary>} />
                          <Route path="/records" element={<PageBoundary name="Download your records"><RecordsDownloadPage /></PageBoundary>} />
                          <Route path="/staff" element={<PageBoundary name="Staff"><StaffList /></PageBoundary>} />
                          <Route path="/staff/:id" element={<PageBoundary name="Staff Profile"><StaffProfile /></PageBoundary>} />
                          <Route path="/rota" element={<PageBoundary name="Rota"><RotaPage /></PageBoundary>} />
                          <Route path="/register" element={<PageBoundary name="Register"><RegisterPage /></PageBoundary>} />
                          <Route path="/funding" element={<PageBoundary name="Funding"><PlanGate feature="Funding"><FundingPage /></PlanGate></PageBoundary>} />
                          <Route path="/reports" element={<PageBoundary name="Reports"><PlanGate feature="Reports"><ReportsPage /></PlanGate></PageBoundary>} />
                          <Route path="/alerts" element={<PageBoundary name="Alerts"><PlanGate feature="Alerts & Reminders" description="Get automated DBS expiry alerts, compliance warnings, and staffing notifications with Professional."><AlertsPage /></PlanGate></PageBoundary>} />
                          <Route path="/compliance" element={<PageBoundary name="Compliance"><PlanGate feature="Compliance"><CompliancePage /></PlanGate></PageBoundary>} />
                          <Route path="/outings" element={<PageBoundary name="Outings"><RequireRole allowed={["super_admin","admin"]}><OutingsPage /></RequireRole></PageBoundary>} />
                          <Route path="/attendance-staffing" element={<PageBoundary name="Attendance & Staffing"><PlanGate feature="Attendance & Staffing"><AttendanceStaffingPage /></PlanGate></PageBoundary>} />
                          <Route path="/planned-changes" element={<PageBoundary name="Planned Changes"><PlannedChangesPage /></PageBoundary>} />
                          <Route path="/additional-days" element={
                            <PageBoundary name="Additional Days">
                              <RequireRole allowed={["super_admin", "admin", "editor", "front_desk"]}>
                                <PlanGate feature="Additional Days" description="Let parents request one-off extra days and extended hours, with staff approval, on Professional.">
                                  <AdditionalDaysPage />
                                </PlanGate>
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/safeguarding-log" element={
                            <PageBoundary name="Accident, Incident & Behaviour Log">
                              <PlanGate feature="Accident, Incident & Behaviour Log" description="Keep the written accident, incident and behaviour record inspectors ask to see, with Professional.">
                                <RequireRole allowed={["super_admin", "admin", "editor", "front_desk"]} allowDsl>
                                  <SafeguardingLogPage />
                                </RequireRole>
                              </PlanGate>
                            </PageBoundary>
                          } />
                          <Route path="/complaints-log" element={
                            <PageBoundary name="Complaints Log">
                                <RequireRole allowed={["super_admin", "admin", "editor"]} allowDsl>
                                  <ComplaintsLogPage />
                                </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/confidential-safeguarding" element={
                            <PageBoundary name="Confidential Safeguarding">
                              <PlanGate feature="Confidential Safeguarding" description="Keep the confidential safeguarding file, LADO and social care records separate and passcode protected, on Professional.">
                                <RequireSafeguardingAccess>
                                  <ConfidentialSafeguardingPage />
                                </RequireSafeguardingAccess>
                              </PlanGate>
                            </PageBoundary>
                          } />





                          <Route path="/access" element={
                            <PageBoundary name="Access Management">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <AccessManagement />
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/access/permissions-guide" element={
                            <PageBoundary name="Permissions Guide">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <PermissionsGuidePage />
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/settings" element={
                            <PageBoundary name="Settings">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <SettingsPage />
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/my-profile" element={<PageBoundary name="My Profile"><MyProfilePage /></PageBoundary>} />
                          <Route path="/security" element={<PageBoundary name="Security"><SecurityPage /></PageBoundary>} />
                          <Route path="/audit-log" element={
                            <PageBoundary name="Audit Log">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <PlanGate feature="Audit Log"><AuditLogPage /></PlanGate>
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/ofsted-readiness" element={
                            <PageBoundary name="Ofsted Readiness">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <PlanGate feature="Ofsted Readiness"><OfstedReadinessPage /></PlanGate>
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/approvals" element={
                            <PageBoundary name="Approvals">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <ApprovalsPage />
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/help" element={<PageBoundary name="Help Manual"><HelpManualPage /></PageBoundary>} />
                          <Route path="/my-rota" element={<PageBoundary name="My Rota"><MyRotaPage /></PageBoundary>} />
                          <Route path="/room-register" element={<PageBoundary name="Room Register"><RoomRegisterPage /></PageBoundary>} />
                          <Route path="/pickup" element={<Navigate to="/pickups/today" replace />} />
                          <Route path="/pickups" element={<Navigate to="/pickups/today" replace />} />
                          <Route path="/pickups/today" element={<PageBoundary name="Today's Pickups"><PlanGate feature="Pickups & Drop-offs"><TodaysPickupsPage /></PlanGate></PageBoundary>} />
                          <Route path="/pickups/monthly" element={<PageBoundary name="Monthly Overview"><PlanGate feature="Pickups & Drop-offs"><MonthlyOverviewPage /></PlanGate></PageBoundary>} />
                          <Route path="/pickups/schools" element={
                            <PageBoundary name="Schools">
                              <RequireRole allowed={["super_admin"]}>
                                <PlanGate feature="Pickups & Drop-offs"><SchoolsPage /></PlanGate>
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/pickups/bookings" element={
                            <PageBoundary name="Booking Management">
                              <RequireRole allowed={["super_admin", "admin"]}>
                                <PlanGate feature="Pickups & Drop-offs"><BookingManagementPage /></PlanGate>
                              </RequireRole>
                            </PageBoundary>
                          } />
                          <Route path="/nursly-admin/migrations" element={<PageBoundary name="Nursly Admin Migrations"><NurslyAdminMigrationsPage /></PageBoundary>} />
                          <Route path="/nursly-admin/error-log" element={<PageBoundary name="Nursly Admin Error Log"><NurslyAdminErrorLogPage /></PageBoundary>} />
                          <Route path="/nursly-admin/feedback" element={<PageBoundary name="Nursly Admin Feedback"><NurslyAdminFeedbackPage /></PageBoundary>} />
                          <Route path="/nursly-admin/debug-emails" element={<PageBoundary name="Nursly Admin Debug Emails"><NurslyAdminDebugEmailsPage /></PageBoundary>} />
                          <Route path="/nursly-admin" element={<Navigate to="/nursly-admin/debug-emails" replace />} />

                          <Route path="/upgrade" element={<PageBoundary name="Upgrade"><UpgradePage /></PageBoundary>} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </Layout>
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
          </AuthProvider>
        </NetworkStatusProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
